import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BASE_URL } from "../../../config/api";
import { formatDateTime } from "../../../Component/Common/formatDateTime";
import axios from "axios";

/* ================= PAGE ================= */

export default function PaymentDetails() {
  const { paymentId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [flow, setFlow] = useState([]);
  const [flowLoading, setFlowLoading] = useState(false);

  /* ================= PAYMENT DETAILS ================= */
  useEffect(() => {

    const fetchDetails = async () => {

      try {

        setLoading(true);

        const response = await axios.get(
          `${BASE_URL}admin/payments/${paymentId}`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        const json = response.data;

        setData(json);

      } catch (e) {

        console.error(e);

      } finally {

        setLoading(false);

      }
    };

    fetchDetails();

  }, [paymentId]);

  /* ================= WARRANTY FLOW ================= */
  useEffect(() => {

  const fetchWarrantyFlow = async () => {

    try {

      setFlowLoading(true);

      const response = await axios.get(
        `${BASE_URL}admin/payments/${paymentId}/warranty-flow`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const json = response.data;

      setFlow(
        json?.flow || []
      );

    } catch (e) {

      console.error(e);

    } finally {

      setFlowLoading(false);

    }
  };

  fetchWarrantyFlow();

}, [paymentId]);

  if (loading) {
    return <PageSkeleton />;
  }

  if (!data) return null;

  let meta = {};
  let raw = {};
  try {
    meta = JSON.parse(data.meta || "{}");
    raw = JSON.parse(data.raw_payload || "{}");
  } catch {}

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 m-3">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* ================= HERO ================= */}
        <div className="rounded-3xl bg-gradient-to-br from-blue-700 to-blue-800 p-6 text-white">
          <p className="text-sm opacity-80">Payment Successful</p>
          <div className="mt-2 text-3xl font-bold">₹{data.amount}</div>

          <div className="mt-4 flex flex-wrap gap-2">
            <GlowPill label={data.status} variant="green" />
            <GlowPill label={data.gateway} variant="gray" />
            <GlowPill label={data.service} variant="blue" />
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <HeroStat label="Payment ID" value={data.payment_id} />
            <HeroStat label="Paid On" value={formatDateTime(data.paid_at)} />
            <HeroStat label="Method" value={raw.method || "-"} />
          </div>
        </div>

        {/* ================= GRID ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT */}
          <div className="lg:col-span-2 space-y-6">
            <GlassCard title="Customer & Device">
              <Info label="Email" value={raw.email} />
              <Info label="Contact" value={raw.contact} />
              <Info label="IMEI" value={meta.imei1} />
            </GlassCard>

            <GlassCard title="Payment Breakdown">
              <Money label="Base Amount" value={raw.base_amount} />
              <Money label="Gateway Fee" value={raw.fee} />
              <Money label="Tax" value={raw.tax} />
              <Money label="Refunded" value={raw.amount_refunded} />
            </GlassCard>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <MiniCard
                label="Transaction Created"
                value={
                  raw.created_at
                    ? formatDateTime(new Date(raw.created_at * 1000))
                    : "-"
                }
              />
              {raw.upi && <MiniCard label="UPI ID" value={raw.upi.vpa} />}
              {raw.acquirer_data && (
                <MiniCard label="Bank RRN" value={raw.acquirer_data.rrn} />
              )}
              <MiniCard label="Currency" value={data.currency} />
            </div>
          </div>

          {/* RIGHT */}
          <div className="space-y-6">
            <GlassCard1 title="Warranty Processing Flow">
              <div className="max-h-[580px] overflow-y-auto overflow-x-hidden p-2">
                {flowLoading ? (
                  <FlowSkeleton />
                ) : flow.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No warranty flow information available
                  </p>
                ) : (
                  <div className="space-y-3">
                    {flow.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start justify-between rounded-lg bg-slate-50 px-4 py-3 ring-1 ring-slate-200"
                      >
                        <div>
                          <p className="font-medium text-slate-900">
                            {item.step.replaceAll("_", " ")}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDateTime(item.created_at)}
                          </p>

                          {item.status === 0 && item.error_message && (
                            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                              <p className="text-xs font-semibold text-red-700 mb-1">
                                Error Details
                              </p>
                              <div className="max-h-[200px] overflow-y-auto overflow-x-hidden">
                                <pre className="whitespace-pre-wrap break-all text-xs text-red-700">
                                  {item.error_message}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>

                        <span
                          className={`ml-4 rounded-full px-3 py-1 text-xs font-semibold ${
                            item.status === 1
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.status === 1 ? "SUCCESS" : "FAILED"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </GlassCard1>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= SKELETONS ================= */

const PageSkeleton = () => (
  <div className="min-h-screen bg-slate-50 px-4 py-10 m-3 animate-pulse">
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="h-40 rounded-3xl bg-slate-200" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SkeletonCard />
          <SkeletonCard />
          <div className="grid grid-cols-2 gap-2">
            <SkeletonMini />
            <SkeletonMini />
            <SkeletonMini />
            <SkeletonMini />
          </div>
        </div>
        <SkeletonCard rows={6} />
      </div>
    </div>
  </div>
);

const FlowSkeleton = () => (
  <div className="space-y-3 animate-pulse">
    {Array.from({ length: 5 }).map((_, i) => (
      <div
        key={i}
        className="rounded-lg bg-slate-100 p-4 ring-1 ring-slate-200"
      >
        <div className="h-3 w-40 bg-slate-200 rounded mb-2" />
        <div className="h-2 w-28 bg-slate-200 rounded" />
      </div>
    ))}
  </div>
);

const SkeletonCard = ({ rows = 4 }) => (
  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
    <div className="h-4 w-40 bg-slate-200 rounded mb-4" />
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-3 bg-slate-200 rounded mb-2" />
    ))}
  </div>
);

const SkeletonMini = () => (
  <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
    <div className="h-3 w-24 bg-slate-200 rounded mb-2" />
    <div className="h-4 w-32 bg-slate-200 rounded" />
  </div>
);

/* ================= UI COMPONENTS ================= */

const GlowPill = ({ label, variant }) => {
  const styles = {
    blue: "border-blue-400 bg-blue-500/10 text-blue-100",
    green: "border-emerald-400 bg-emerald-500/10 text-emerald-100",
    gray: "border-slate-400 bg-slate-500/10 text-slate-100",
  };
  return (
    <span className={`rounded-full border px-3 py-1 text-xs ${styles[variant]}`}>
      {label}
    </span>
  );
};

const GlassCard = ({ title, children }) => (
  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
    <p className="mb-4 text-sm font-semibold text-slate-700">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const GlassCard1 = ({ title, children }) => (
  <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
    <p className="mb-4 text-sm font-semibold text-slate-700">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">{children}</div>
  </div>
);

const HeroStat = ({ label, value }) => (
  <div className="rounded-xl bg-white/20 px-4 py-3">
    <p className="text-xs opacity-80">{label}</p>
    <p className="mt-1 font-semibold">{value}</p>
  </div>
);

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500 uppercase">{label}</p>
    <p className="font-semibold">{value || "-"}</p>
  </div>
);

const Money = ({ label, value }) => (
  <Info label={label} value={`₹${((value || 0) / 100).toFixed(2)}`} />
);

const MiniCard = ({ label, value }) => (
  <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
    <p className="text-xs text-slate-500 uppercase">{label}</p>
    <p className="mt-1 font-semibold">{value}</p>
  </div>
);





// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import { BASE_URL } from "../../../config/api";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatDateTime } from "../../../Component/Common/formatDateTime";

// export default function PaymentDetails() {
//   const { paymentId } = useParams();
//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(false);

//   useEffect(() => {
//     const fetchDetails = async () => {
//       try {
//         setLoading(true);
//         const res = await fetch(`${BASE_URL}admin/payments/${paymentId}`);
//         const json = await res.json();
//         if (!res.ok) throw new Error("Fetch failed");
//         setData(json);
//       } catch (e) {
//         console.error(e);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchDetails();
//   }, [paymentId]);

//   const GlowPill = ({
//   label,
//   variant = "blue", // blue | green | gray
// }) => {
//   const styles = {
//     blue: `
//       border-blue-400
//       bg-blue-500/10
//       text-blue-200
//       shadow-[0_0_0_1px_rgba(59,130,246,0.4),0_8px_20px_rgba(59,130,246,0.35)]
//     `,
//     green: `
//       border-emerald-400
//       bg-emerald-500/10
//       text-emerald-200
//       shadow-[0_0_0_1px_rgba(16,185,129,0.4),0_8px_20px_rgba(16,185,129,0.35)]
//     `,
//     gray: `
//       border-slate-400
//       bg-slate-500/10
//       text-slate-200
//       shadow-[0_0_0_1px_rgba(148,163,184,0.35),0_8px_20px_rgba(148,163,184,0.3)]
//     `,
//   };

//   return (
//     <span
//       className={`
//         inline-flex items-center
//         rounded-full border
//         px-3 py-1
//         text-[10px] font-black uppercase tracking-tight
//         backdrop-blur-md
//         transition-all duration-200
//         hover:scale-[1.03]
//         hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_12px_28px_rgba(0,0,0,0.35)]
//         ${styles[variant]}
//       `}
//     >
//       {label}
//     </span>
//   );
// };



//   if (loading) {
//     return (
//       <div className="py-14 flex justify-center">
//         <RegularLoader />
//       </div>
//     );
//   }

//   if (!data) return null;

//   let meta = {};
//   let raw = {};
//   try {
//     meta = JSON.parse(data.meta || "{}");
//     raw = JSON.parse(data.raw_payload || "{}");
//   } catch {}

//   return (
//     <div className="min-h-screen bg-slate-100 px-4 py-8 m-3">
//       <div className="max-w-6xl mx-auto space-y-8">

//         {/* ================= HERO CARD ================= */}
//         <div className="relative rounded-3xl bg-blue-900 p-6 text-white shadow-xl">
//           <p className="text-sm opacity-80">Payment Successful</p>

//           <div className="mt-2 text-3xl font-bold">
//             ₹{data.amount}
//           </div>

//           <div className="mt-3 flex flex-wrap gap-2">
//             <GlowPill label={data.status} variant="green" />
//             <GlowPill label={data.gateway} variant="gray" />
//             <GlowPill label={data.service} variant="blue" />
//             </div>


//           <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
//             <HeroStat label="Payment ID" value={data.payment_id} />
//             <HeroStat label="Paid On" value={formatDateTime(data.paid_at)} />
//             <HeroStat label="Method" value={raw.method || "-"} />
//           </div>
//         </div>

//         {/* ================= DASHBOARD GRID ================= */}
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

//           {/* LEFT COLUMN */}
//           <div className="lg:col-span-2 space-y-6">

//             <GlassCard title="Customer & Device">
//               <Info label="Email" value={raw.email} />
//               <Info label="Contact" value={raw.contact} />
//               <Info label="IMEI" value={meta.imei1} />
//               <Info label="Product ID" value={meta.product_id} />
//               <Info label="Brand ID" value={meta.brand_id} />
//               <Info label="Category ID" value={meta.category_id} />
//             </GlassCard>

//             <GlassCard title="Payment Breakdown">
//               <Money label="Base Amount" value={raw.base_amount} />
//               <Money label="Gateway Fee" value={raw.fee} />
//               <Money label="Tax" value={raw.tax} />
//               <Money label="Refunded" value={raw.amount_refunded} />
//             </GlassCard>
//           </div>

//           {/* RIGHT COLUMN */}
//           <div className="space-y-6">

//             <MiniCard
//               label="Transaction Created"
//               value={
//                 raw.created_at
//                   ? formatDateTime(new Date(raw.created_at * 1000))
//                   : "-"
//               }
//             />

//             {raw.upi && (
//               <MiniCard label="UPI ID" value={raw.upi.vpa} />
//             )}

//             {raw.acquirer_data && (
//               <MiniCard
//                 label="Bank RRN"
//                 value={raw.acquirer_data.rrn}
//               />
//             )}

//             <MiniCard
//               label="Currency"
//               value={data.currency}
//             />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ================= UI COMPONENTS ================= */

// const GlassCard = ({ title, children }) => (
//   <div className="rounded-2xl bg-white p-6 shadow-md">
//     <p className="mb-4 text-sm font-semibold text-slate-700">
//       {title}
//     </p>
//     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//       {children}
//     </div>
//   </div>
// );

// const HeroStat = ({ label, value }) => (
//   <div className="rounded-xl bg-white/15 px-4 py-3 backdrop-blur-sm">
//     <p className="text-xs opacity-80">{label}</p>
//     <p className="mt-1 font-semibold">{value}</p>
//   </div>
// );

// const Info = ({ label, value }) => (
//   <div>
//     <p className="text-xs text-slate-500">{label}</p>
//     <p className="font-medium text-slate-900">{value || "-"}</p>
//   </div>
// );

// const Money = ({ label, value }) => (
//   <Info label={label} value={`₹${(value || 0) / 100}`} />
// );

// const MiniCard = ({ label, value }) => (
//   <div className="rounded-2xl bg-white p-5 shadow-md">
//     <p className="text-xs text-slate-500">{label}</p>
//     <p className="mt-1 font-semibold text-slate-900">{value}</p>
//   </div>
// );





// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { BASE_URL } from "../../../config/api";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatDateTime } from "../../../Component/Common/formatDateTime";

// export default function PaymentDetails() {
//   const { paymentId } = useParams();
//   const navigate = useNavigate();

//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(false);

//   /* ---------------- FETCH DETAILS ---------------- */
//   useEffect(() => {
//     const fetchDetails = async () => {
//       try {
//         setLoading(true);

//         const res = await fetch(
//           `${BASE_URL}admin/payments/${paymentId}`
//         );
//         const json = await res.json();

//         if (!res.ok) {
//           throw new Error(json?.message || "Failed to fetch payment");
//         }

//         setData(json);
//       } catch (err) {
//         console.error("Payment details error:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchDetails();
//   }, [paymentId]);

//   if (loading) {
//     return (
//       <div className="py-10 flex justify-center">
//         <RegularLoader />
//       </div>
//     );
//   }

//   if (!data) return null;

//   /* ---------------- PARSE META & RAW ---------------- */
//   let meta = {};
//   let raw = {};

//   try {
//     meta = data.meta ? JSON.parse(data.meta) : {};
//     raw = data.raw_payload ? JSON.parse(data.raw_payload) : {};
//   } catch {
//     meta = {};
//     raw = {};
//   }

//   /* ---------------- UI ---------------- */
//   return (
//     <div className="space-y-5">
//       {/* HEADER */}
//       <div className="flex items-center gap-3">
//         <h2 className="text-lg font-semibold">Payment Details</h2>
//       </div>

//       {/* BASIC INFO */}
//       <SectionCard title="Basic Information">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//           <Info label="Payment ID" value={data.payment_id} />
//           <Info label="Gateway" value={data.gateway} />
//           <Info label="Service">
//             <Badge color="blue">{data.service}</Badge>
//           </Info>
//           <Info label="Status">
//             <Badge color="green">{data.status}</Badge>
//           </Info>
//           <Info label="Amount" value={`₹${data.amount}`} />
//           <Info label="Currency" value={data.currency} />
//           <Info label="Paid At" value={formatDateTime(data.paid_at)} />
//         </div>
//       </SectionCard>

//       {/* META INFO */}
//       <SectionCard title="Meta Information">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//           <Info label="IMEI" value={meta.imei1} />
//           <Info label="Product ID" value={meta.product_id} />
//           <Info label="Brand ID" value={meta.brand_id} />
//           <Info label="Category ID" value={meta.category_id} />
//           <Info label="Company ID" value={meta.company_id} />
//           <Info label="Retailer ID" value={meta.retailer_id} />
//           <Info label="Agent ID" value={meta.agent_id} />
//         </div>
//       </SectionCard>

//       {/* GATEWAY PAYMENT DETAILS */}
//       <SectionCard title="Gateway · Payment Details">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//           <Info label="Razorpay ID" value={raw.id} />
//           <Info label="Method" value={raw.method} />
//           <Info label="Description" value={raw.description} />
//           <Info label="Captured" value={raw.captured ? "Yes" : "No"} />
//           <Info label="International" value={raw.international ? "Yes" : "No"} />
//           <Info
//             label="Created At"
//             value={
//               raw.created_at
//                 ? formatDateTime(new Date(raw.created_at * 1000))
//                 : "-"
//             }
//           />
//         </div>
//       </SectionCard>

//       {/* AMOUNT BREAKDOWN */}
//       <SectionCard title="Amount Breakdown">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//           <Info label="Base Amount" value={`₹${raw.base_amount / 100 || 0}`} />
//           <Info label="Fee" value={`₹${raw.fee / 100 || 0}`} />
//           <Info label="Tax" value={`₹${raw.tax / 100 || 0}`} />
//           <Info
//             label="Refunded Amount"
//             value={`₹${raw.amount_refunded / 100 || 0}`}
//           />
//           <Info label="Currency" value={raw.currency} />
//         </div>
//       </SectionCard>

//       {/* CUSTOMER INFO */}
//       <SectionCard title="Customer Information">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//           <Info label="Email" value={raw.email} />
//           <Info label="Contact" value={raw.contact} />
//           <Info label="VPA" value={raw.vpa || raw.upi?.vpa} />
//         </div>
//       </SectionCard>

//       {/* UPI DETAILS */}
//       {raw.upi && (
//         <SectionCard title="UPI Details">
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//             <Info label="VPA" value={raw.upi.vpa} />
//             <Info label="Flow" value={raw.upi.flow} />
//           </div>
//         </SectionCard>
//       )}

//       {/* ACQUIRER DETAILS */}
//       {raw.acquirer_data && (
//         <SectionCard title="Bank / Acquirer Details">
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//             <Info label="RRN" value={raw.acquirer_data.rrn} />
//             <Info
//               label="UPI Transaction ID"
//               value={raw.acquirer_data.upi_transaction_id}
//             />
//           </div>
//         </SectionCard>
//       )}

//       {/* GATEWAY NOTES */}
//       {raw.notes && (
//         <SectionCard title="Gateway Notes">
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//             <Info label="Project" value={raw.notes.project} />
//             <Info label="Service" value={raw.notes.service} />
//             <Info label="IMEI" value={raw.notes.imei1} />
//             <Info label="Product ID" value={raw.notes.product_id} />
//             <Info label="Brand ID" value={raw.notes.brand_id} />
//             <Info label="Category ID" value={raw.notes.category_id} />
//           </div>
//         </SectionCard>
//       )}
//     </div>
//   );
// }

// /* ---------------- HELPER ---------------- */
// const Info = ({ label, value, children }) => (
//   <div>
//     <p className="text-xs text-slate-500">{label}</p>
//     <p className="font-medium text-slate-800">
//       {children || value || "-"}
//     </p>
//   </div>
// );


// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { BASE_URL } from "../../../config/api";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatDateTime } from "../../../Component/Common/formatDateTime";

// export default function PaymentDetails() {
//   const { paymentId } = useParams();
//   console.log("payment ID: ", paymentId)
//   const navigate = useNavigate();

//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(false);

//   useEffect(() => {
//     const fetchDetails = async () => {
//       try {
//         setLoading(true);

//         const res = await fetch(
//           `${BASE_URL}admin/payments/${paymentId}`
//         );
//         const json = await res.json();

//         if (!res.ok) {
//           throw new Error(json?.message || "Failed to fetch payment");
//         }

//         setData(json);
//       } catch (err) {
//         console.error("Payment details error:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchDetails();
//   }, [paymentId]);

//   if (loading) {
//     return (
//       <div className="py-10 flex justify-center">
//         <RegularLoader />
//       </div>
//     );
//   }

//   if (!data) return null;

//   let meta = {};
//   let raw = {};

//   try {
//     meta = data.meta ? JSON.parse(data.meta) : {};
//     raw = data.raw_payload ? JSON.parse(data.raw_payload) : {};
//   } catch {
//     meta = {};
//     raw = {};
//   }

//   return (
//     <div className="space-y-4">
//       {/* HEADER */}
//       <div className="flex items-center gap-3">
//         <button
//           onClick={() => navigate(-1)}
//           className="text-sm text-blue-600 hover:underline"
//         >
//           ← Back
//         </button>
//         <h2 className="text-lg font-semibold">Payment Details</h2>
//       </div>

//       {/* BASIC INFO */}
//       <SectionCard title="Basic Information">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//           <Info label="Payment ID" value={data.payment_id} />
//           <Info label="Gateway" value={data.gateway} />
//           <Info label="Method" value={raw.method || "-"} />
//           <Info label="Amount" value={`₹${data.amount}`} />
//           <Info label="Currency" value={data.currency} />
//           <Info label="Service">
//             <Badge color="blue">{data.service}</Badge>
//           </Info>
//           <Info label="Status">
//             <Badge color="green">{data.status}</Badge>
//           </Info>
//           <Info label="Paid At" value={formatDateTime(data.paid_at)} />
//         </div>
//       </SectionCard>

//       {/* META INFO */}
//       <SectionCard title="Meta Information">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
//           <Info label="IMEI" value={meta.imei1} />
//           <Info label="Product ID" value={meta.product_id} />
//           <Info label="Brand ID" value={meta.brand_id} />
//           <Info label="Category ID" value={meta.category_id} />
//           <Info label="Company ID" value={meta.company_id} />
//           <Info label="Retailer ID" value={meta.retailer_id} />
//           <Info label="Agent ID" value={meta.agent_id} />
//         </div>
//       </SectionCard>

//       {/* RAW PAYLOAD */}
//       <SectionCard title="Gateway Payload">
//         <pre className="text-xs bg-slate-100 p-4 rounded-md overflow-auto">
//           {JSON.stringify(raw, null, 2)}
//         </pre>
//       </SectionCard>
//     </div>
//   );
// }

// /* --------- Helper --------- */
// const Info = ({ label, value, children }) => (
//   <div>
//     <p className="text-xs text-slate-500">{label}</p>
//     <p className="font-medium text-slate-800">
//       {children || value || "-"}
//     </p>
//   </div>
// );
